import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Instagram, Linkedin, Facebook, MapPin, Loader2 } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT, SETTORE_OPTIONS, SERVIZIO_OPTIONS, BUDGET_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props} aria-hidden="true">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1 0-5.19c.27 0 .53.04.78.12V9.66a5.76 5.76 0 0 0-.78-.05 5.66 5.66 0 1 0 5.66 5.66V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.3 4.3 0 0 1-3.22-1.48Z" />
  </svg>
);

const CONTACT_ROWS = [
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testId: "contact-email-link" },
  { icon: Phone, label: "Telefono", value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phoneRaw}`, testId: "contact-phone-link" },
  { icon: MessageCircle, label: "WhatsApp", value: CONTACT.phoneDisplay, href: CONTACT.whatsapp, testId: "contact-whatsapp-link" },
  { icon: Instagram, label: "Instagram", value: "@startpoint.agency", href: CONTACT.instagram, testId: "contact-instagram-link" },
  { icon: TikTokIcon, label: "TikTok", value: "@startpoint.agency", href: CONTACT.tiktok, testId: "contact-tiktok-link" },
  { icon: Linkedin, label: "LinkedIn", value: "Starting Point Agency", href: CONTACT.linkedin, testId: "contact-linkedin-link" },
  { icon: Facebook, label: "Facebook", value: "Starting Point Agency", href: CONTACT.facebook, testId: "contact-facebook-link" },
];

const INITIAL = {
  nome: "", azienda: "", email: "", telefono: "",
  settore: "", servizio: "", messaggio: "", budget: "", privacy: false,
};

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Devi accettare la privacy policy per continuare.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Richiesta inviata. Ti ricontattiamo a breve per la tua consulenza gratuita.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova o scrivici direttamente via email.");
    } finally {
      setSending(false);
    }
  };

  const fieldCls = "h-12 rounded-lg border-slate-200 bg-white text-navy placeholder:text-slate-400 focus-visible:ring-brand-blue";

  return (
    <section id="contatti" data-testid="contact-section" className="bg-brand-light py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Kicker>Contatti</Kicker>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.0] tracking-tight text-navy sm:text-6xl">
              OGNI GRANDE PROGETTO PARTE DA UNA <span className="text-brand-gradient">CONVERSAZIONE.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">
              Raccontaci la tua attività, i tuoi obiettivi e ciò che vorresti migliorare. Partiremo da lì per capire quali opportunità hanno senso per il tuo business.
            </p>
            <p className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <MapPin className="h-4 w-4 text-brand-blue" /> {CONTACT.location}
            </p>
          </Reveal>
          <div className="mt-10 space-y-1">
            {CONTACT_ROWS.map((row, i) => (
              <Reveal key={row.label} delay={0.05 * i}>
                <a
                  href={row.href}
                  data-testid={row.testId}
                  target={row.href.startsWith("http") ? "_blank" : undefined}
                  rel={row.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="group flex items-center gap-4 border-b border-navy/10 py-4 transition-colors hover:border-brand-blue/50"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-navy/10 bg-white text-navy transition-colors group-hover:border-brand-blue group-hover:text-brand-blue">
                    <row.icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] tracking-[0.25em] text-slate-500">{row.label.toUpperCase()}</span>
                    <span className="block text-sm font-semibold text-navy group-hover:text-brand-blue">{row.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="border border-slate-200/80 bg-white p-7 shadow-[0_30px_80px_-40px_rgba(10,18,41,0.3)] sm:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="nome">Nome e cognome *</Label>
                  <Input id="nome" data-testid="contact-nome-input" required value={form.nome} onChange={(e) => set("nome", e.target.value)} className={`mt-1.5 ${fieldCls}`} placeholder="Mario Rossi" />
                </div>
                <div>
                  <Label htmlFor="azienda">Azienda</Label>
                  <Input id="azienda" data-testid="contact-azienda-input" value={form.azienda} onChange={(e) => set("azienda", e.target.value)} className={`mt-1.5 ${fieldCls}`} placeholder="Nome della tua attività" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={`mt-1.5 ${fieldCls}`} placeholder="nome@azienda.it" />
                </div>
                <div>
                  <Label htmlFor="telefono">Telefono</Label>
                  <Input id="telefono" data-testid="contact-telefono-input" type="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={`mt-1.5 ${fieldCls}`} placeholder="+39 ..." />
                </div>
                <div>
                  <Label htmlFor="settore">Settore</Label>
                  <Select value={form.settore} onValueChange={(v) => set("settore", v)}>
                    <SelectTrigger id="settore" data-testid="contact-settore-select" className={`mt-1.5 ${fieldCls}`}>
                      <SelectValue placeholder="Seleziona il settore" />
                    </SelectTrigger>
                    <SelectContent>
                      {SETTORE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="servizio">Servizi di interesse</Label>
                  <Select value={form.servizio} onValueChange={(v) => set("servizio", v)}>
                    <SelectTrigger id="servizio" data-testid="contact-servizio-select" className={`mt-1.5 ${fieldCls}`}>
                      <SelectValue placeholder="Seleziona un servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVIZIO_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="messaggio">Messaggio</Label>
                <Textarea id="messaggio" data-testid="contact-messaggio-input" rows={4} value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)} className="mt-1.5 rounded-lg border-slate-200 bg-white text-navy placeholder:text-slate-400 focus-visible:ring-brand-blue" placeholder="Raccontaci il tuo punto di partenza: dove sei oggi e dove vuoi arrivare." />
              </div>
              <div className="mt-5">
                <Label htmlFor="budget">Possibilità d'investimento indicativa <span className="font-normal text-slate-400">(facoltativa)</span></Label>
                <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                  <SelectTrigger id="budget" data-testid="contact-budget-select" className={`mt-1.5 ${fieldCls}`}>
                    <SelectValue placeholder="Seleziona un intervallo" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGET_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-6 flex items-start gap-3">
                <Checkbox id="privacy" data-testid="contact-privacy-checkbox" checked={form.privacy} onCheckedChange={(v) => set("privacy", v === true)} required aria-label="Accetto la privacy policy" />
                <Label htmlFor="privacy" className="text-xs font-normal leading-relaxed text-slate-500">
                  Ho letto e accetto la <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-blue">Privacy Policy</a> e acconsento al trattamento dei dati per essere ricontattato. *
                </Label>
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="group mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-brand-gradient px-7 py-4 font-display text-sm font-bold tracking-wide text-white shadow-[0_12px_36px_-10px_rgba(37,99,235,0.5)] transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {sending ? "INVIO IN CORSO..." : "RICHIEDI LA TUA CONSULENZA GRATUITA"}
                {!sending && (
                  <svg viewBox="0 0 24 24" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 17 17 7" /><path d="M8 7h9v9" />
                  </svg>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
